import React, { useState, useEffect } from 'react';
import Cookies from 'js-cookie';
import DashBoardNavBar from '../components/DashBoardNavBar.tsx';
import Navbar from '../components/Navbar.tsx';
import { Particles } from '../components/Particles.tsx';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  Chip,
  Card,
  CardActions,
  CardContent,
  Grid,
  Typography,
  CircularProgress,
  Autocomplete,
  Snackbar,
  Alert
} from '@mui/material';

interface ContentSubmission {
  id: number;
  title: string;
  shortDescription: string;
  url: string;
  category: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  adminNotes: string | null;
}

interface Report {
  reportId: number; 
  submissionId: number;
  userId: number;
  reason: string;
  status: string;
  createdAt: string;
}

const AdminContent: React.FC = () => {
  const [openCreate, setOpenCreate] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
  const [pendingContents, setPendingContents] = useState<ContentSubmission[]>([]);
  const [allContents, setAllContents] = useState<ContentSubmission[]>([]);
  const [selectedContent, setSelectedContent] = useState<ContentSubmission | null>(null);
  const [formData, setFormData] = useState({
    title: '',
    shortDescription: '',
    url: '',
    category: '',
    status: 'PENDING',
    adminNotes: ''
  });
  const [loading, setLoading] = useState(true);
  const token = Cookies.get('token');
  const isAuthenticated = !!token;
  const [openReports, setOpenReports] = useState(false);
  const [reports, setReports] = useState<Report[]>([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
  


  const fetchReports = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/v1/admin/getreports`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setReports(data);
      } else {
        console.error('Failed to load reports:', response.status);
      }
    } catch (error) {
      console.error('Error loading reports:', error);
    }
  };
  
  const handleOpenReports = () => {
    fetchReports();
    setOpenReports(true);
  };

  const handleSolveReport = async (reportId: number) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/v1/admin/switchreportstatus`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          submissionId: reportId, // A backend submissionId-t vár
          status: "RESOLVED"
        })
      });
  
      if (response.ok) {
        setReports(reports.filter(report => report.reportId !== reportId));
      }
    } catch (error) {
      console.error('Error updating report status:', error);
    }
  };
  
  const handleClearReports = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/v1/admin/clearreports`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setSnackbarMessage(data.message);
        setSnackbarSeverity('success');
        // Újratöltjük a jelentéseket
        fetchReports();
      } else {
        setSnackbarMessage('Failed to clear reports!');
        setSnackbarSeverity('error');
      }
    } catch (error) {
      console.error('Error clearing reports:', error);
      setSnackbarMessage('Error clearing reports!');
      setSnackbarSeverity('error');
    } finally {
      setSnackbarOpen(true);
    }
  };

  // Pending tartalmak betöltése és szűrése (csak PENDING státusz)
  useEffect(() => {
    const fetchPendingContent = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/v1/admin/pending`, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        });
        if (response.ok) {
          const data = await response.json();
          // Csak a PENDING státuszú elemek mentése
          const pending = data.filter((item: ContentSubmission) => item.status === 'PENDING');
          setPendingContents(pending);
        } else {
          console.error('Failed to load pending content:', response.status);
        }
      } catch (error) {
        console.error('Error loading pending content:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPendingContent();
  }, [token]);

  // Az összes tartalom betöltése az editáláshoz
  useEffect(() => {
    const fetchAllContent = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/v1/admin/content`, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        });
        if (response.ok) {
          const data = await response.json();
          setAllContents(data);
        } else {
          console.error('Failed to load content:', response.status);
        }
      } catch (error) {
        console.error('Error loading content:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAllContent();
  }, [token]);

  // Tartalom létrehozása
  const handleCreate = async () => {
    try {
      const { adminNotes, ...createData } = formData; // Exclude adminNotes for creation
      const response = await fetch(`${process.env.REACT_APP_API_URL}/v1/admin/content`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(createData)
      });
      
      if (response.ok) {
        setOpenCreate(false);
        setFormData({ title: '', shortDescription: '', url: '', category: '', status: 'PENDING', adminNotes: '' });
        refreshPendingContents();
      } else {
        console.error('Failed to create content:', response.status);
      }
    } catch (error) {
      console.error('Error creating content:', error);
    }
  };

  // Tartalom státusz módosítása (APPROVED / REJECTED)
  const handleStatusChange = async (id: number, status: 'APPROVED' | 'REJECTED') => {
    const endpoint = status === 'APPROVED' ? 'approve' : 'reject';
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/v1/admin/${endpoint}/${id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${Cookies.get('token')}`
        }
      });
      if (response.ok) {
        // Újratöltjük a pending tartalmakat
        const updatedResponse = await fetch(`${process.env.REACT_APP_API_URL}/v1/admin/pending`, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${Cookies.get('token')}`
          }
        });
        if (updatedResponse.ok) {
          const updatedContent = await updatedResponse.json();
          const pending = updatedContent.filter((item: ContentSubmission) => item.status === 'PENDING');
          setPendingContents(pending);
        }
      } else {
        console.error(`Failed to update status (${status}):`, response.status);
      }
    } catch (error) {
      console.error(`Error updating status (${status}):`, error);
    }
  };

  // Tartalom szerkesztése
  const handleEdit = async () => {
    if (!selectedContent) return;
  
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/v1/admin/content/${selectedContent.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });
  
      if (response.ok) {
        setOpenEdit(false);
        // Frissítjük az összes tartalmat az edit-hez
        const updatedResponse = await fetch(`${process.env.REACT_APP_API_URL}/v1/admin/content`, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        });
        if (updatedResponse.ok) {
          const updatedContent = await updatedResponse.json();
          setAllContents(updatedContent);
        }
        // Frissítjük a pending tartalmakat is
        refreshPendingContents();
      } else {
        console.error('Failed to update content:', response.status);
      }
    } catch (error) {
      console.error('Error updating content:', error);
    }
  };

  const refreshPendingContents = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/v1/admin/pending`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      if(response.ok){
        const data = await response.json();
        const pending = data.filter((item: ContentSubmission) => item.status === 'PENDING');
        setPendingContents(pending);
      } else {
        console.error('Failed to refresh pending contents:', response.status);
      }
    } catch (error) {
      console.error('Error refreshing pending contents:', error);
    }
  };

  const refreshAllContents = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/v1/admin/content`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setAllContents(data);
      } else {
        console.error('Failed to refresh all contents:', response.status);
      }
    } catch (error) {
      console.error('Error refreshing all contents:', error);
    }
  };
  
  const handleDeleteConfirmed = async () => {
    if (!selectedContent) return;
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/v1/admin/deletecontent`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ id: selectedContent.id })
      });
      if (response.ok) {
        setOpenEdit(false);
        setOpenDeleteConfirm(false);
        refreshAllContents();
        refreshPendingContents();
      } else {
        console.error('Failed to delete content:', response.status);
      }
    } catch (error) {
      console.error('Error deleting content:', error);
    }
  };
  
  return (
    <div style={{ position: 'relative', minHeight: '100vh' }}>
      <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: -1 }}>
        <Particles />
      </div>

      {isAuthenticated ? <DashBoardNavBar /> : <Navbar />}

      <div style={{ padding: '2rem', position: 'relative', top: '3rem' }}>
        <Typography variant="h4" gutterBottom>Content Management</Typography>
        
        <div style={{ marginBottom: '2rem' }}>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={() => setOpenCreate(true)}
            style={{ marginRight: '1rem' }}
          >
            Create New Content
          </Button>
          <Button 
            variant="outlined" 
            color="secondary"
            onClick={() => { refreshAllContents(); setOpenEdit(true); }}
          >
            Edit Existing Content
          </Button>
          <Button 
            variant="contained" 
            color="info" 
            onClick={handleOpenReports}
            style={{ marginLeft: '1rem' }}
          >
            Reports
          </Button>
          <Button 
            variant="contained" 
            color="error" 
            onClick={handleClearReports}
          >
            Clear Reports
          </Button>
        </div>



        {/* Create Content Dialog */}
        <Dialog open={openCreate} onClose={() => setOpenCreate(false)}>
          <DialogTitle>Create New Content</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Title"
              fullWidth
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            />
            <TextField
              margin="dense"
              label="Description"
              multiline
              rows={4}
              fullWidth
              onChange={(e) => setFormData({ ...formData, shortDescription: e.target.value })}
            />
            <TextField
              margin="dense"
              label="URL"
              fullWidth
              onChange={(e) => setFormData({ ...formData, url: e.target.value })}
            />
            <TextField
              margin="dense"
              label="Category"
              fullWidth
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
            />
            <TextField
              margin="dense"
              select
              label="Status"
              fullWidth
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
            >
              <MenuItem value="PENDING">PENDING</MenuItem>
              <MenuItem value="APPROVED">APPROVED</MenuItem>
              <MenuItem value="REJECTED">REJECTED</MenuItem>
            </TextField>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenCreate(false)}>Cancel</Button>
            <Button onClick={handleCreate} color="primary">Create</Button>
          </DialogActions>
        </Dialog>

        {/* Edit Content Dialog */}
        <Dialog open={openEdit} onClose={() => setOpenEdit(false)}>
        <DialogTitle>Edit Content</DialogTitle>
        <DialogContent>
          <Autocomplete
            options={allContents}
            getOptionLabel={(option) => `${option.title} (${option.status})`}
            filterOptions={(options, { inputValue }) =>
              options.filter(option =>
                option.title.toLowerCase().includes(inputValue.toLowerCase())
              )
            }
            renderInput={(params) => (
              <TextField {...params} label="Select content to edit" variant="outlined" />
            )}
            onChange={(event, newValue) => {
              setSelectedContent(newValue);
              if (newValue) {
                setFormData({
                  title: newValue.title,
                  shortDescription: newValue.shortDescription,
                  url: newValue.url,
                  category: newValue.category,
                  status: newValue.status,
                  adminNotes: newValue.adminNotes || '' // Set adminNotes from selected content
                });
              }
            }}
            fullWidth
          />

            {selectedContent && (
              <>
                <TextField
                  margin="dense"
                  label="Title"
                  fullWidth
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                />
                <TextField
                  margin="dense"
                  label="Description"
                  multiline
                  rows={4}
                  fullWidth
                  value={formData.shortDescription}
                  onChange={(e) => setFormData({ ...formData, shortDescription: e.target.value })}
                />
                <TextField
                  margin="dense"
                  label="URL"
                  fullWidth
                  value={formData.url}
                  onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                />
                <TextField
                  margin="dense"
                  label="Category"
                  fullWidth
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                />
                <TextField
                margin="dense"
                select
                label="Status"
                fullWidth
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              >
                <MenuItem value="PENDING">PENDING</MenuItem>
                <MenuItem value="APPROVED">APPROVED</MenuItem>
                <MenuItem value="REJECTED">REJECTED</MenuItem>
              </TextField>
              <TextField
                margin="dense"
                label="Admin Note"
                multiline
                rows={2}
                fullWidth
                value={formData.adminNotes}
                onChange={(e) => setFormData({ ...formData, adminNotes: e.target.value })}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEdit(false)}>Cancel</Button>
          <Button onClick={() => setOpenDeleteConfirm(true)} variant="contained" color="error">Delete</Button>
          <Button onClick={handleEdit} color="primary">Save</Button>
        </DialogActions>
      </Dialog>

      {/* Reports Dialog */}
      <Dialog open={openReports} onClose={() => setOpenReports(false)} maxWidth="md" fullWidth>
        <DialogTitle>Reports</DialogTitle>
        <DialogContent dividers>
          {reports.length === 0 ? (
            <Typography>No reports found.</Typography>
          ) : (
            <Grid container spacing={2}>
              {reports.map((report: any) => (
                <Grid item xs={12} md={6} key={report.reportId}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6">Report #{report.reportId}</Typography>
                      <Typography variant="body2" color="textSecondary" gutterBottom>
                        Submission ID: {report.submissionId} | User ID: {report.userId}
                      </Typography>
                      <Typography variant="body1">{report.reason}</Typography>
                      <Typography variant="caption" color="textSecondary">
                        Created: {new Date(report.createdAt).toLocaleDateString()}
                      </Typography>
                    </CardContent>
                    <CardActions>
                      {report.status === "OPEN" && (
                        <Button 
                          variant="contained" 
                          color="success"
                          onClick={() =>  handleSolveReport(report.reportId)}
                        >
                          Resolve
                        </Button>
                      )}
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenReports(false)}>Close</Button>
        </DialogActions>
      </Dialog>

        <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>


        <Dialog open={openDeleteConfirm} onClose={() => setOpenDeleteConfirm(false)}>
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <Typography>Are you sure you want to delete this content?</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDeleteConfirm(false)}>No</Button>
            <Button onClick={handleDeleteConfirmed} variant="contained" color="error">Yes</Button>
          </DialogActions>
        </Dialog>

        {/* Pending Content List */}
        <Typography variant="h6" gutterBottom style={{ marginTop: '2rem' }}>
          Pending Submissions ({pendingContents.length})
        </Typography>
        
        {loading ? (
          <CircularProgress />
        ) : (
          <div style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid #ccc', borderRadius: '4px' }}>
            <List>
              {pendingContents.map(content => (
                <ListItem key={content.id} divider>
                  <ListItemText
                    primary={content.title}
                    secondary={
                      <>
                        <div>{content.shortDescription}</div>
                        <div style={{ marginTop: '0.5rem' }}>
                          <Chip label={content.category} size="small" />
                          <Chip label={content.status} size="small" color="warning" style={{ marginLeft: '0.5rem' }} />
                        </div>
                      </>
                    }
                    secondaryTypographyProps={{ component: 'div' }}
                  />
                  <div>
                    <Button 
                      variant="contained" 
                      color="success" 
                      size="small"
                      onClick={() => handleStatusChange(content.id, 'APPROVED')}
                      style={{ marginRight: '0.5rem' }}
                    >
                      Approve
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      onClick={() => handleStatusChange(content.id, 'REJECTED')}
                    >
                      Reject
                    </Button>
                  </div>
                </ListItem>
              ))}
            </List>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminContent;